(function () {
  class Injector {
    constructor(weightedSymbols, rules, logger) {
      this._symbolRules = rules || [];
      this._symbolWeights = weightedSymbols;
      this.logger = logger;
      this.reset();
    }

    reset() {
      this._targetSymbols = [];
      this._targetSymbolsLeftTilLand = 0;
      this._minTravelDisance = 0;
      this._proceedingSymbols = [];
      this._numSymbolsInjected = 0;
      this._savedState = {};
    }

    initialise() {
      this._numSymbolsInjected = 0;
    }

    increaseSymbolsInjected() {
      ++this._numSymbolsInjected;
    }

    saveState() {
      this._savedState = {};
      this._savedState.targetSymbols = JSON.parse(
        JSON.stringify(this._targetSymbols)
      );
      this._savedState.proceedingSymbols = JSON.parse(
        JSON.stringify(this._proceedingSymbols)
      );
    }

    restoreState() {
      this._targetSymbols = JSON.parse(
        JSON.stringify(this._savedState.targetSymbols)
      );
      this._proceedingSymbols = JSON.parse(
        JSON.stringify(this._savedState.proceedingSymbols)
      );
    }

    getNumSymbolsInjected() {
      return this._numSymbolsInjected;
    }

    addSymbolRule(symbolRule) {
      this._symbolRules.push(symbolRule);
    }

    setSymbolRules(rules) {
      this._symbolRules = rules;
    }

    setMinTravelDistance(minTravelDistance) {
      this._minTravelDisance = minTravelDistance;
    }

    getMinTravelDistance() {
      return this._minTravelDisance;
    }

    canLand() {
      return this._targetSymbolsLeftTilLand === 0;
    }

    handOverPreceedingResponisiblity(otherInjector) {
      this._proceedingSymbols = otherInjector._proceedingSymbols;
      otherInjector._proceedingSymbols = [];
    }

    // this is used for example when the post injector still has above symbols in its target symbols
    // that need to be moved to the preinjecotr for the next spin
    transferTargetSymbolsToPreceedingSymbols(otherInjector) {
      this._proceedingSymbols = this._proceedingSymbols.concat(
        otherInjector._targetSymbols
      );
      otherInjector._targetSymbols = [];
      otherInjector._targetSymbolsLeftTilLand = 0;
    }

    setTargetSymbols(targetSymbols, targetSymbolsLeftTilLand) {
      this._targetSymbols = targetSymbols;
      this._targetSymbolsLeftTilLand = targetSymbolsLeftTilLand;
    }

    doesSymbolPassAllRules(symbol, previousWindowSymbols, nextSymbol) {
      for (let i = 0; i < this._symbolRules.length; ++i) {
        if (
          !this._symbolRules[i].isSymbolValid(
            symbol,
            previousWindowSymbols,
            nextSymbol
          )
        )
          return false;
      }
      return true;
    }

    getMaxSpacesNeededForRule(symbol) {
      let res = 0;
      for (let i = 0; i < this._symbolRules.length; ++i) {
        res = Math.max(res, this._symbolRules[i].getNumSymbolsNeeded(symbol));
      }
      return res;
    }

    getNumSymbolsToReserve(symbol) {
      let res = 0;
      for (let i = 0; i < this._symbolRules.length; ++i) {
        res = Math.max(
          res,
          this._symbolRules[i].getNumSymbolsToReserve(symbol)
        );
      }
      return res;
    }

    getRandomSymbol(
      previousWindowSymbols,
      currentSlotsAvaliableAvailable,
      firstTargetSymbol
    ) {
      let symbol = 0;
      let excludedSymbols = [];
      let maxSpacesNeededForRule = 0;
      let nextSymbol = -1;
      do {
        this._symbolWeights.pickOutcome(function (sym) {
          symbol = sym;
        }, excludedSymbols);

        // dont pick this symbol again
        excludedSymbols.push(symbol);

        // safety net, if there are no valid sybmols, shouldnt be the case, just return the last one. Breaking these rules isnt the end of the world, or even compliance related.
        if (
          this._symbolWeights.getTotalAvailableOutcomes() ===
          excludedSymbols.length
        ) {
          this.logger('COULDNT FIND A VALID SYMBOL');
          return symbol;
        }
        maxSpacesNeededForRule = this.getMaxSpacesNeededForRule(symbol);

        nextSymbol = -1;
        if (maxSpacesNeededForRule === currentSlotsAvaliableAvailable) {
          nextSymbol = firstTargetSymbol;
        }
      } while (
        maxSpacesNeededForRule > currentSlotsAvaliableAvailable ||
        !this.doesSymbolPassAllRules(symbol, previousWindowSymbols, nextSymbol)
      );
      return symbol;
    }

    getReservedSymbols(symbol, spacesNeeded, currentSlotsAvalilable) {
      for (let i = 0; i < this._symbolRules.length; ++i) {
        if (
          this._symbolRules[i].getNumSymbolsToReserve(symbol) === spacesNeeded
        ) {
          let numToGenerate = Math.min(spacesNeeded, currentSlotsAvalilable); // just in case we dont have enough slots available, should never happen
          return this._symbolRules[i].getReservedSymbols(
            symbol,
            numToGenerate - 1
          ); // -1 because we are already using the first
        }
      }
      console.log('ERROR IN getReservedSymbols');
      return [];
    }

    handleRulesForThisSymbol(symbol, currentSlotsAvalilable) {
      let spacesNeeded = this.getNumSymbolsToReserve(symbol);
      if (spacesNeeded > 0)
        this._proceedingSymbols = this.getReservedSymbols(
          symbol,
          spacesNeeded,
          currentSlotsAvalilable
        );
    }

    getValidSymbol(previousWindowSymbols, reelId) {
      let res = 0;
      if (this._proceedingSymbols.length > 0) {
        // are we forcing in symbols created by a rule? For example, symbols part of a group
        if (this._minTravelDisance > 0) {
          // it is valid to use rule symbols during the postinjector symbol fillers (none target symbols), so we still need to decrement the count
          --this._minTravelDisance;
        }
        res = this._proceedingSymbols.pop();
      } else if (this._minTravelDisance > 0) {
        // post injector symbol fillers (none target symbols). Keep picking a random symbol until we move on to the target symbols
        --this._minTravelDisance;

        res = this.getRandomSymbol(
          previousWindowSymbols,
          this._minTravelDisance + 1,
          this._targetSymbols[this._targetSymbols.length - 1]
        );
        this.handleRulesForThisSymbol(res, this._minTravelDisance + 1);
      } else if (this._targetSymbols.length > 0) {
        // time to force the target symbols in. We might be left with some symbols in the target array which are the above symbols that have yet to come into view
        --this._targetSymbolsLeftTilLand;
        res = this._targetSymbols.pop();
      } else {
        // just a normal preinjector spin
        if (this._targetSymbolsLeftTilLand > 0)
          // this is a bit of a hack, and I dont understant why it is needed. I believe it is for the preapperture symbol after all targets have spun in. Needs investigating
          --this._targetSymbolsLeftTilLand;

        res = this.getRandomSymbol(previousWindowSymbols, Number.MAX_VALUE, -1); // right now we have unlimited space as we are in the post injector

        this.handleRulesForThisSymbol(res, Number.MAX_VALUE);
      }
      this.increaseSymbolsInjected();
      return res;
    }

    getNumSymbolsLeftToMeetRules() {
      return this._proceedingSymbols.length;
    }

    calculateProceedingSymbols(symbolArray) {
      if (this._symbolRules.length === 0) return [];

      let additionalSymbolsNeeded = [];
      for (let i = 0; i < this._symbolRules.length; ++i) {
        let additionSymbols =
          this._symbolRules[i].getAdditionalSymbolsNeededToSatisfyRule(
            symbolArray
          );
        additionalSymbolsNeeded.push(additionSymbols);
      }

      let longestSymbolsIdx = 0;
      for (let i = 0; i < additionalSymbolsNeeded.length; ++i) {
        if (
          additionalSymbolsNeeded[i].length >
          additionalSymbolsNeeded[longestSymbolsIdx].length
        )
          longestSymbolsIdx = i;
      }
      return additionalSymbolsNeeded[longestSymbolsIdx];
    }

    testReelBandPassesAllRules(reelBand) {
      for (let i = 0; i < this._symbolRules.length; ++i) {
        if (!this._symbolRules[i].testReelBandPassesRule(reelBand))
          return false;
      }
      return true;
    }
  }

  class PreInjector extends Injector {
    constructor(weightedSymbols, rules, logger) {
      super(weightedSymbols, rules, logger);
    }
    getSymbolsLeft() {
      return 0;
    }

    initialise(symbolsStillInWindow) {
      super.initialise();
      let newProceedingSymbols = this.calculateProceedingSymbols(
        symbolsStillInWindow.concat(this._proceedingSymbols)
      );
      this._proceedingSymbols =
        this._proceedingSymbols.concat(newProceedingSymbols);
    }
  }

  class PostInjector extends Injector {
    constructor(weightedSymbols, rules, logger) {
      super(weightedSymbols, rules, logger);
    }

    getSymbolsLeft() {
      return this._targetSymbolsLeftTilLand;
    }
  }

  class DynamicInjector {
    constructor(weightedSymbols, rules, logger) {}

    //getSymbolsLeft(){
    //    return this._targetSymbolsLeftTilLand;
    //}

    //initialise(symbolsStillInWindow){
    //    super.initialise();
    //    let newProceedingSymbols = this.calculateProceedingSymbols(symbolsStillInWindow.concat(this._proceedingSymbols));
    //    this._proceedingSymbols = this._proceedingSymbols.concat(newProceedingSymbols);
    //}
  }

  module.exports = function () {
    return {
      pre: function (weightedSymbols, rules, logger) {
        return new PreInjector(weightedSymbols, rules, logger);
      },
      post: function (weightedSymbols, rules, logger) {
        return new PostInjector(weightedSymbols, rules, logger);
      },
      dynamic: function (weightedSymbols, rules, logger) {
        return new DynamicInjector(weightedSymbols, rules, logger);
      },
    };
  };
})();
